import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

/**
 * Shared, token-driven state primitives for the data-dense surfaces of the
 * platform. FL jobs are long-running and asynchronous, so every list / panel /
 * card should be able to render a consistent loading / empty / error / live
 * state instead of each surface inventing its own.
 *
 * Purely presentational — these components hold no data-fetching logic and make
 * no backend calls. Colors/spacing come from the design tokens (CSS vars in
 * index.css / theme.js). Motion respects the global prefers-reduced-motion rule.
 */

const wrapSx = (minHeight) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  gap: 1,
  px: 3,
  py: 4,
  minHeight,
  width: "100%",
});

/** Centered spinner + label — use while a panel's primary data is loading. */
export const LoadingState = ({ label = "Loading…", minHeight = 160, size = 22 }) => (
  <Box sx={wrapSx(minHeight)} role="status" aria-live="polite" aria-busy="true">
    <CircularProgress size={size} thickness={5} sx={{ color: "var(--fx-secondary)" }} />
    <Typography sx={{ color: "var(--fx-text-secondary)", fontSize: "0.8125rem", fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

/** Empty placeholder — no data yet, or filters matched nothing. */
export const EmptyState = ({
  icon,
  title = "Nothing here yet",
  description,
  action,
  onAction,
  actionLabel,
  minHeight = 180,
}) => (
  <Box sx={wrapSx(minHeight)}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--fx-text-muted)",
        bgcolor: "var(--fx-subtle)",
        border: "1px solid var(--fx-border)",
        mb: 0.5,
      }}
    >
      {icon || <InboxOutlinedIcon sx={{ fontSize: 22 }} />}
    </Box>
    <Typography sx={{ color: "var(--fx-text-primary)", fontSize: "0.9375rem", fontWeight: 600 }}>
      {title}
    </Typography>
    {description && (
      <Typography sx={{ color: "var(--fx-text-secondary)", fontSize: "0.8125rem", lineHeight: 1.5, maxWidth: 360 }}>
        {description}
      </Typography>
    )}
    {action
      ? <Box sx={{ mt: 1 }}>{action}</Box>
      : onAction && actionLabel && (
          <Button
            size="small"
            variant="outlined"
            onClick={onAction}
            sx={{ mt: 1 }}
          >
            {actionLabel}
          </Button>
        )}
  </Box>
);

/** Error placeholder with optional retry. */
export const ErrorState = ({
  title = "Something went wrong",
  description,
  onRetry,
  retryLabel = "Retry",
  minHeight = 180,
}) => (
  <Box sx={wrapSx(minHeight)} role="alert">
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--fx-danger)",
        bgcolor: "rgba(220, 38, 38, 0.08)",
        border: "1px solid rgba(220, 38, 38, 0.20)",
        mb: 0.5,
      }}
    >
      <ErrorOutlineRoundedIcon sx={{ fontSize: 22 }} />
    </Box>
    <Typography sx={{ color: "var(--fx-text-primary)", fontSize: "0.9375rem", fontWeight: 600 }}>
      {title}
    </Typography>
    {description && (
      <Typography sx={{ color: "var(--fx-text-secondary)", fontSize: "0.8125rem", lineHeight: 1.5, maxWidth: 380 }}>
        {description}
      </Typography>
    )}
    {onRetry && (
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
        onClick={onRetry}
        sx={{ mt: 1 }}
      >
        {retryLabel}
      </Button>
    )}
  </Box>
);

/**
 * Compact "live / streaming" badge — a pulsing dot + label for surfaces that
 * are actively updating (training running, polling, streaming logs). The pulse
 * is CSS-based, so it's automatically neutralized under prefers-reduced-motion.
 */
export const LiveBadge = ({
  label = "Live",
  // text color meets AA for small labels; the dot keeps the vivid status hue
  color = "var(--fx-success-text)",
  dotColor = "var(--fx-success)",
  bg = "rgba(22, 163, 74, 0.10)",
  title,
}) => (
  <Box
    title={title}
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      px: 1,
      py: 0.3,
      borderRadius: 999,
      bgcolor: bg,
      color,
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      lineHeight: 1,
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: dotColor,
        boxShadow: `0 0 0 0 ${dotColor}`,
        animation: "fxLivePulse 1.8s ease-in-out infinite",
      }}
    />
    {label}
  </Box>
);

/**
 * Unified status badge (1.4) — ONE component, four semantic variants, all
 * identical in size, padding, radius, and border treatment. Use this for every
 * status pill across the app (Online, Connected, Pending, Not saved, Not checked
 * yet, OPTIONAL, counts) so they read as one system instead of per-screen
 * inventions.
 *
 *   success  — Online, Connected, Passed       (teal/green tint)
 *   warning  — Pending, Selection incomplete    (amber tint)
 *   danger   — Not saved, Failed                (red tint)
 *   neutral  — Not checked yet, OPTIONAL, counts (gray tint)
 *
 * Text always uses the darker stop of the same hue (--fx-*-text), never plain
 * white or plain gray on the tinted fill. An optional leading dot is available.
 */
const STATUS_VARIANTS = {
  success: {
    color: "var(--fx-success-text)",
    bg: "rgba(22, 163, 74, 0.12)",
    border: "rgba(22, 163, 74, 0.30)",
    dot: "var(--fx-success)",
  },
  warning: {
    color: "var(--fx-warning-text)",
    bg: "rgba(217, 119, 6, 0.12)",
    border: "rgba(217, 119, 6, 0.30)",
    dot: "var(--fx-warning)",
  },
  danger: {
    color: "var(--fx-danger-text)",
    bg: "rgba(220, 38, 38, 0.12)",
    border: "rgba(220, 38, 38, 0.30)",
    dot: "var(--fx-danger)",
  },
  neutral: {
    color: "var(--fx-text-secondary)",
    bg: "var(--fx-subtle)",
    border: "var(--fx-border-strong)",
    dot: "var(--fx-text-muted)",
  },
};

export const StatusBadge = ({
  variant = "neutral",
  label,
  children,
  dot = false,
  icon = null,
  title,
  sx = {},
}) => {
  const v = STATUS_VARIANTS[variant] || STATUS_VARIANTS.neutral;
  return (
    <Box
      component="span"
      title={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: 22,
        px: 1,
        borderRadius: 999,
        border: `1px solid ${v.border}`,
        bgcolor: v.bg,
        color: v.color,
        fontSize: "0.6875rem",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: v.dot, flexShrink: 0 }}
        />
      )}
      {icon}
      {label || children}
    </Box>
  );
};

/** Tiny inline spinner for buttons / inline affordances. */
export const InlineSpinner = ({ size = 14, color = "inherit" }) => (
  <CircularProgress size={size} thickness={5} sx={{ color }} />
);

export default { LoadingState, EmptyState, ErrorState, LiveBadge, StatusBadge, InlineSpinner };
